import { useAuth } from '@sp/data/auth';
import { ROUTES, useRedirectAfterLogin } from '@sp/util/router';
import { ReactElement, useEffect } from 'react';
import { useHref, useLocation, useNavigate } from 'react-router-dom';

export function useRedirectNonAuthenticated(redirectTo: string = ROUTES.LOGIN_PATH): void {
  const { isLoading, isLoggedIn } = useAuth();
  const { setRedirect } = useRedirectAfterLogin();
  const currentHref = useHref(useLocation());
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isLoggedIn) {
      setRedirect(currentHref);
      navigate(redirectTo, { replace: true });
    }
  });
}

export function AuthenticatedRoute({
  children,
  redirectTo,
}: {
  readonly children: ReactElement;
  readonly redirectTo?: string;
}): ReactElement | null {
  const { user, isLoading } = useAuth();

  useRedirectNonAuthenticated(redirectTo);

  if (isLoading || user == null) {
    return null;
  }

  return children;
}

export function useRedirectAuthenticated(redirectTo?: string): void {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const { setRedirect, redirectUrl } = useRedirectAfterLogin();

  useEffect(() => {
    if (!isLoading && user != null) {
      navigate(redirectTo ?? redirectUrl, { replace: true });
      setRedirect('/');
    }
  });
}

export function NonAuthenticatedRoute({
  children,
  redirectTo,
}: {
  readonly children: ReactElement;
  readonly redirectTo?: string;
}): ReactElement | null {
  const { isLoading, user } = useAuth();

  useRedirectAuthenticated(redirectTo);

  if (isLoading || user != null) {
    return null;
  }

  return children;
}
